import { getLocalStorage } from '../../views/apps/engagement/marketing-automation/journey-builder/utils'

/**
 * @typedef {{ x: number, y: number }} DiagramPosition
 */

/**
 * Initial state for the palette.
 * @typedef {Object} PaletteState
 * @type {Object}
 * @property {boolean} expanded - Indicates if the palette is expanded.
 * @property {string} searchTerm - The search term for filtering palette items.
 */
const paletteState = {
  expanded: true,
  searchTerm: '',
  nodes: [],
  nodeMap: {}
}

/**
 * Initial state for the history (undo/redo).
 * @typedef {Object} HistoryState
 * @type {Object}
 * @property {boolean} hasUndo - Indicates if there are actions to undo.
 * @property {boolean} hasRedo - Indicates if there are actions to redo.
 * @property {string} lastPatchId - The ID of the last patch applied.
 */
const historyState = {
  hasUndo: false,
  hasRedo: false,
  lastPatchId: ''
}

/**
 * Initial state for the Properties Panel.
 * @typedef {Object} PropertiesPanelState
 * @type {Object}
 * @property {boolean} expanded - Indicates if the palette is expanded.
 */
const propertiesPanelState = {
  expanded: false
}

/**
 * Initial state for the error node popover.
 * @typedef {Object} ErrorNodeState
 * @type {Object}
 * @property {boolean} open - Indicates if the error popover is open.
 * @property {string} nodeId - The ID of the node with errors.
 * @property {string[]} errors - The list of errors for the node.
 * @property {string} label - The label of the node with errors.
 * */
const errorNodeState = {
  open: false,
  nodeId: '',
  errors: [],
  label: ''
}

/**
 * Initial state for the selection.
 * @typedef {Object} SelectionState
 * @type {Object}
 * @property {string[]} nodes - The IDs of the selected nodes.
 * @property {string[]} links - The IDs of the selected links.
 */
const selectionState = {
  nodes: [],
  links: []
}

/**
 * Initial state for the context menu.
 * @typedef {Object} ContextMenuState
 * @type {Object}
 * @property {boolean} open - Indicates if the context menu is open.
 * @property {DiagramPosition|null} uiPosition - The ui position of the context menu.
 * @property {DiagramPosition|null} canvasPosition - The canvas position of the context menu.
 * @property {ContextMenuElementType|null} elementType - The element type that triggered the context menu.
 */
const contextMenuState = {
  open: false,
  uiPosition: null,
  canvasPosition: null,
  elementType: null
}

/**
 * Initial state for the halo.
 * @typedef {Object} HaloState
 * @property {string} searchText - The search text for filtering halo nodes.
 * @property {string} source - The source node ID for the halo.
 * @property {PaletteNodes} compatibleNodes - The list of compatible nodes for the halo.
 */
const haloState = {
  compatibleNodes: null,
  searchTerm: '',
  source: null
}

/** @typedef {{[key: string]: WorkflowNode}} NodesType */
/** @typedef {{[key: string]: WorkflowEdge}} LinksType */

/**
 * The workflow state containing nodes and links.
 * @typedef {{
 * nodes: NodesType;
 * links: LinksType;
 * name: string;
 * }} WorkflowState
 * */

/**
 * The diagram changes state.
 * @typedef {Object} ChangesState
 * @property {{ nodes: CanvasWorkflowNode[], links: WorkflowEdge[]}} added
 * @property {{ nodes: CanvasWorkflowNode[], links: WorkflowEdge[]}} updated
 * @property {{ nodes: string[], links: string[]}} removed
 * @property {boolean} reset
 * */

/**
 *The initial state for the diagram clipboard.
 * @typedef {{ nodes: WorkflowNode[], links: WorkflowEdge[] }|null} ClipboardState
 */

/**
 * The UI state.
 * @typedef {Object} UiState
 * @type {Object}
 * @property {PaletteState} palette - The palette state, retrieved from local storage.
 * @property {boolean} mockMode - Whether mock mode is enabled, retrieved from local storage.
 * @property {number|string} zoomScale - The zoom scale, can be a number or 'auto'.
 * @property {HistoryState} history - The history state for undo/redo functionality.
 * @property {SelectionState} selection - The selection state containing selected nodes and links.
 * @property {PropertiesPanelState} propertiesPanel - The properties panel state.
 * @property {ContextMenuState} contextMenu - The context menu state.
 * @property {ClipboardState} clipboard - he clipboard state
 * */

/**
 *The initial state for the application.
 * @typedef {{
 * workflow: WorkflowState;
 * ui: UiState;
 * changes: ChangesState|null;
 * mode: WorkflowMode;
 * }} JBState
 */

/**
 * Gets the initial state for the application.
 * @returns JBState
 */
export const getInitialState = () => ({
  workflow: { journeyId: '', nodes: {}, links: {}, name: '', lastChipIndex: 0 },
  ui: {
    palette: getLocalStorage('palette', paletteState), // get palette config from local storage
    mockMode: getLocalStorage('mockMode', false), // get mock mode from local storage
    zoomScale: 1, // number | 'auto'
    history: historyState,
    selection: selectionState,
    propertiesPanel: propertiesPanelState,
    contextMenu: contextMenuState,
    clipboard: null,
    errorNode: errorNodeState,
    infoPanel: { open: false, nodeId: '', target: '' },
    scrollToNodeId: null,
    halo: haloState,
    errorStack: {},
    nodeReportPopover: { open: false, nodeData: null, position: null }
  },
  changes: null,
  mode: 'edit',
  user: {
    data: [],
    total: 1,
    params: {},
    allData: []
  }
})
